import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useVisibility } from "../contexts/VisibilityContext";
import { devLog, logError } from "../utils/logger";

/**
 * Movement threshold precision for coordinate rounding.
 * Controls how far the user must move before triggering a notes refetch.
 * This prevents unnecessary API calls from GPS jitter while the user is stationary.
 *
 * Decimal places → approximate distance:
 *   1 → ~11.1 km    (city-level)
 *   2 → ~1.1 km     (neighborhood-level)
 *   3 → ~111 m      (street-level) ← default
 *   4 → ~11 m       (building-level)
 *   5 → ~1.1 m      (no practical threshold)
 */
const MOVEMENT_PRECISION = 3;

/**
 * Position stability threshold in meters (~1000ft).
 * The backend randomizes locations within ~500ft of the true position,
 * so two random samples can differ by up to ~1000ft (the full diameter).
 * Using the diameter as threshold ensures the first-seen position is
 * "locked in" for the session — no visual jumping on refetches.
 */
const POSITION_STABILITY_THRESHOLD_M = 305;

/**
 * Approximate distance in meters between two lat/lng points.
 * Uses equirectangular approximation — accurate enough for <1km distances.
 */
function approxDistanceM(lat1, lng1, lat2, lng2) {
  const toRad = Math.PI / 180;
  const dx = (lng2 - lng1) * toRad * Math.cos(((lat1 + lat2) / 2) * toRad) * 6371000;
  const dy = (lat2 - lat1) * toRad * 6371000;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Stabilize note positions across refetches.
 * If a note's new position is within the randomization threshold of its
 * previously cached position, reuse the cached position so the marker
 * doesn't jump on the map.
 *
 * @param {Array} notes - Fresh notes from the API
 * @param {Map} cache - Mutable Map of noteId → { latitude, longitude }
 * @returns {Array} Notes with stabilized positions
 */
function stabilizePositions(notes, cache, fullSet = false) {
  const currentIds = new Set();

  const stabilized = notes.map((note) => {
    currentIds.add(note.id);
    const cached = cache.get(note.id);

    if (cached) {
      const dist = approxDistanceM(
        cached.latitude, cached.longitude,
        note.latitude, note.longitude,
      );

      if (dist <= POSITION_STABILITY_THRESHOLD_M) {
        // Within randomization range — keep cached position
        return { ...note, latitude: cached.latitude, longitude: cached.longitude };
      }
    }

    // New note or moved beyond threshold — update cache
    cache.set(note.id, { latitude: note.latitude, longitude: note.longitude });
    return note;
  });

  // Only clean up stale entries when processing a full refetch.
  // Partial updates (e.g. createNote with 1 note) must NOT purge
  // the cache for other notes.
  if (fullSet) {
    for (const id of cache.keys()) {
      if (!currentIds.has(id)) cache.delete(id);
    }
  }

  return stabilized;
}

/**
 * Hook for fetching nearby notes (Home.js)
 * Filters notes by geographic location and radius.
 * Uses coordinate rounding to prevent refetches from GPS jitter —
 * only triggers an API call when the user moves beyond ~111m (at precision 3).
 */
export function useNearbyNotes(userLocation, radius = 1, unit = 'miles') {
  // Get current user from auth context
  const { user } = useAuth();
  const { isVisible } = useVisibility();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingNote, setFetchingNote] = useState(false);
  const [error, setError] = useState(null);

  // Track whether periodic polling has run once (to avoid double fetch with location-change effect)
  const hasPolledOnce = useRef(false);

  // Cache for stabilizing randomized note positions across refetches
  const positionCacheRef = useRef(new Map());

  // Extract precise coordinates
  const lat = userLocation?.latitude;
  const lng = userLocation?.longitude;

  // Round coordinates to movement threshold precision.
  // Only changes when user moves beyond the threshold distance.
  const multiplier = Math.pow(10, MOVEMENT_PRECISION);
  const stableLat = lat != null ? Math.round(lat * multiplier) / multiplier : null;
  const stableLng = lng != null ? Math.round(lng * multiplier) / multiplier : null;

  devLog("useNearbyNotes hook:", {
    userLocation,
    radius,
    stableLat,
    stableLng,
    movementPrecision: MOVEMENT_PRECISION,
    api,
    userId: user?.id,
  });

  if (!api) {
    logError("api is not defined!");
    throw new Error("api is not available");
  }

  // Fetch nearby notes — depends on stable (rounded) coordinates, not raw object.
  // The actual API call still uses precise lat/lng for accurate results.
  const fetchNotes = useCallback(async () => {
    if (stableLat == null || stableLng == null) {
      devLog("useNearbyNotes: No user location, skipping fetch");
      setLoading(false);
      return;
    }

    devLog("useNearbyNotes: Fetching notes for location:", { lat, lng, radius });

    try {
      setLoading(true);
      setError(null);
      // Use precise coordinates for the API call, not the rounded ones
      const response = await api.getNearbyNotes({
        latitude: lat,
        longitude: lng,
        radius,
        unit: unit === 'miles' ? 'mi' : unit,
      });
      devLog("useNearbyNotes: Fetched notes:", response.data);
      const stable = stabilizePositions(response.data, positionCacheRef.current, true);
      setNotes(stable);
    } catch (err) {
      logError("useNearbyNotes: Error fetching notes:", err);
      setError(err.message || "Failed to load nearby notes");
    } finally {
      setLoading(false);
    }
  }, [stableLat, stableLng, radius, unit, lat, lng]);

  /**
   * Fetch when location changes beyond the movement threshold.
   * fetchNotes depends on stableLat/stableLng (rounded coordinates),
   * so it only recreates when the user moves ~111m+ (at precision 3).
   * GPS jitter and minor position changes are filtered out.
   */
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Periodic refetch to pick up notes created by other users.
  // Runs every 60s independently of location changes.
  // Pauses when the tab is hidden and resumes with an immediate fetch when visible.
  const NOTES_REFRESH_INTERVAL = 60000;
  useEffect(() => {
    if (stableLat == null || stableLng == null || !isVisible) return;

    // Immediate fetch when tab becomes visible again (skip on first mount — handled by location-change effect)
    if (hasPolledOnce.current) {
      fetchNotes();
    }
    hasPolledOnce.current = true;

    const interval = setInterval(() => {
      devLog("useNearbyNotes: Periodic refetch");
      fetchNotes();
    }, NOTES_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchNotes, stableLat, stableLng, isVisible]);

  // Create a new note and add it to the list
  const createNote = useCallback(
    async (noteData) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        // Pass userId to API (mirrors real backend behavior)
        const response = await api.createNote(noteData);
        // Cache the backend's randomized position so refetches don't jump
        const [stabilized] = stabilizePositions([response.data], positionCacheRef.current);
        setNotes((prev) => [stabilized, ...prev]);
        return response.data;
      } catch (err) {
        setError(err.message || "Failed to create note");
        throw err;
      }
    },
    [user],
  );

  /**
   * Fetch a single note by ID with fresh data from backend
   * Useful for refreshing note data when opening modal
   * Also updates the note in the notes list if it exists
   * @param {string} noteId - The ID of the note to fetch
   * @returns {Promise<Object>} The fresh note data
   */
  const fetchNote = useCallback(async (noteId) => {
    try {
      setFetchingNote(true);
      setError(null);

      // Optimistically mark as viewed in local state
      setNotes((prev) =>
        prev.map((note) =>
          note.id === noteId ? { ...note, isViewedByCurrentUser: true } : note,
        ),
      );

      const response = await api.getNote(noteId);

      // Update the note in the list with fresh data.
      // Force isViewedByCurrentUser: true since we just opened it — the backend
      // records the view on GET /notes/:id but the response may arrive before
      // the flag is persisted, so we don't let it overwrite our optimistic value.
      // Preserve the cached (stabilized) position so the marker doesn't jump.
      const cached = positionCacheRef.current.get(noteId);
      setNotes((prev) =>
        prev.map((note) =>
          note.id === noteId
            ? {
                ...response.data,
                isViewedByCurrentUser: true,
                ...(cached && { latitude: cached.latitude, longitude: cached.longitude }),
              }
            : note,
        ),
      );

      return response.data;
    } catch (err) {
      logError("Failed to fetch note:", err);
      setError(err.message || "Failed to load note");
      throw err;
    } finally {
      setFetchingNote(false);
    }
  }, []);

  /**
   * Delete a note by ID
   * Optimistically removes from local state and calls API
   * @param {string} noteId - The ID of the note to delete
   */
  const deleteNote = useCallback(
    async (noteId) => {
      try {
        // Optimistically remove from UI
        setNotes((prev) => prev.filter((note) => note.id !== noteId));

        // Call API to delete
        await api.deleteNote(noteId);
      } catch (err) {
        logError("Failed to delete note:", err);
        setError(err.message || "Failed to delete note");
        // Refetch to restore state on error
        fetchNotes();
        throw err;
      }
    },
    [fetchNotes],
  );

  return {
    notes,
    loading,
    fetchingNote,
    error,
    refetch: fetchNotes,
    createNote,
    fetchNote,
    deleteNote,
  };
}
